import {createApi} from '@reduxjs/toolkit/query/react';
import {axiosBaseQuery, REFETCH_POLICY} from 'src/axiosBaseQuery';
import {
    CreateTariffRequest,
    DefaultResponse,
    PageTariffDTO,
    TariffByIdRequest,
    TariffDTO,
    TariffPointSearchRequest,
    ToogleStatusRequest,
    UpdateTariffRequest,
} from 'src/types';
import {TariffStats} from 'src/types/stats';
import {getBaseURL} from 'src/utils/helper';

// initialize an empty api service that we'll inject endpoints into later as needed
export const tariffsApi = createApi({
    reducerPath: 'tariffsApi',
    ...REFETCH_POLICY,
    baseQuery: axiosBaseQuery({
        baseUrl: String(getBaseURL() + '/tariffs'),
    }),
    tagTypes: ['Tariffs', 'Tariff'],
    endpoints: (build) => ({
        // The same body the list is fetched with, so the block counts exactly the set the list
        // shows — the server drops the tiles' own filter and forces the tenant.
        getTariffStats: build.query<TariffStats, TariffPointSearchRequest['data']>({
            query: (data) => ({url: '/stats/summary', method: 'post', data}),
            providesTags: ['Tariffs'],
        }),
        // The plans on offer, for somebody choosing one — active only, no management.
        // The catalogue's own /search asks for tariffs.search, which customers stopped holding in
        // V1.159.0: a driver picking a plan holds tariffs.browse and nothing else.
        getAvailableTariffs: build.query<PageTariffDTO, TariffPointSearchRequest>({
            query: ({data, params}) => ({url: '/available', method: 'post', data, params}),
            providesTags: ['Tariffs'],
        }),
        getTariffs: build.query<PageTariffDTO, TariffPointSearchRequest>({
            query: ({data, params}) => ({url: '/search', method: 'post', data, params}),
            providesTags: (result) => {
                if (result?.content) {
                    return [
                        {type: 'Tariffs' as const, id: 'LIST'},
                        ...result.content.map((tariff) => ({
                            type: 'Tariffs' as const,
                            id: tariff?.id,
                        })),
                    ];
                }
                return [{type: 'Tariffs' as const, id: 'LIST'}, 'Tariffs' as const];
            },
        }),
        addTariff: build.mutation<TariffDTO, CreateTariffRequest>({
            query: (data) => ({url: '', method: 'post', data}),
            invalidatesTags: ['Tariffs'],
        }),
        toggleStatus: build.mutation<TariffDTO, ToogleStatusRequest>({
            query: ({params, tariffId}) => ({
                url: `/${tariffId}/toggleStatus`,
                method: 'post',
                params,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: 'Tariffs' as const, id: arg.tariffId},
                {type: 'Tariffs' as const, id: 'LIST'},
            ],
        }),
        updateTariff: build.mutation<DefaultResponse, UpdateTariffRequest>({
            query: ({data, tariffId}) => ({url: `/${tariffId}`, method: 'put', data}),
            invalidatesTags: (_result, _error, arg) => [
                {type: 'Tariffs' as const, id: arg.tariffId},
                {type: 'Tariffs' as const, id: 'LIST'},
            ],
        }),
        deleteTariff: build.mutation<DefaultResponse, TariffByIdRequest>({
            query: ({businessId, tariffId}) => ({
                url: `/${tariffId}`,
                method: 'delete',
                params: {businessId},
            }),
            invalidatesTags: [{type: 'Tariffs' as const, id: 'LIST'}],
        }),
        getTariffById: build.query<TariffDTO, TariffByIdRequest>({
            query: ({businessId, tariffId}) => ({
                url: `/${tariffId}`,
                method: 'get',
                params: {businessId},
            }),
            providesTags: (_result, _error, arg) => [{type: 'Tariffs' as const, id: arg.tariffId}],
        }),
    }),
});
